import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'

/**
 * Landing page for Supabase's password-reset email link. Supabase puts the
 * recovery tokens in the URL hash; its client picks them up automatically
 * and sets the session. We just let the user enter a new password and
 * call updateUser({ password }).
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [ready, setReady] = useState(false)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Wait for Supabase to process the hash on mount. If no session is
  // established, the link was invalid or already used.
  useEffect(() => {
    let mounted = true
    supabase.auth.getSession().then((res: { data: { session: { access_token: string } | null } }) => {
      if (!mounted) return
      if (!res.data.session) setError('This reset link is invalid or has already been used.')
      setReady(true)
    })
    return () => {
      mounted = false
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setLoading(true)
    setError(null)
    const { error: err } = await supabase.auth.updateUser({ password })
    if (err) {
      setError(err.message)
      setLoading(false)
      return
    }
    setSuccess(true)
    setLoading(false)
    // Give the user a moment to see the success, then redirect.
    setTimeout(() => navigate('/'), 1500)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: 380, p: 4, borderRadius: 3 }}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main' }}>
            Reset Password
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Choose a new password for your account
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Password updated — redirecting…
          </Alert>
        )}

        {!ready ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <TextField
              label="New password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              autoFocus
              autoComplete="new-password"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              fullWidth
              required
              autoComplete="new-password"
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading || success}
              sx={{ fontWeight: 700, py: 1.5 }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Update Password'}
            </Button>
          </>
        )}
      </Paper>
    </Box>
  )
}
